/**
 * SMBPrime marketing home page.
 * Copy and structure from: docs/roadmap/SMbPrime Website Copy Internal assessment.md
 * Theme: docs/2026-Branding
 * Last Updated: 2026-02-26
 */

import { Link } from 'react-router-dom';
import {
  Target,
  FileCheck,
  Users,
  Mail,
  Calendar,
  ClipboardList,
  ArrowRight,
  CheckCircle2,
  XCircle,
  HelpCircle,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';

import heroImage from '@assets/hero-image.png';
import handshakeImage from '@assets/images/handshake-2.png';

/* ----- 2026 imagery: SVG patterns and illustrations ----- */

function HeroPattern(): JSX.Element {
  return (
    <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
      <defs>
        <pattern id="hero-grid" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="0.5" />
        </pattern>
        <linearGradient id="hero-shine" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(59,130,246,0.15)" />
          <stop offset="100%" stopColor="transparent" />
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#hero-grid)" />
      <rect width="100%" height="100%" fill="url(#hero-shine)" />
    </svg>
  );
}

function HeroIllustration(): JSX.Element {
  return (
    <div className="absolute right-0 top-1/2 -translate-y-1/2 w-64 sm:w-80 lg:w-96 opacity-20 pointer-events-none hidden lg:block" aria-hidden="true">
      <svg viewBox="0 0 400 300" fill="none" className="w-full h-auto">
        <circle cx="320" cy="80" r="120" stroke="white" strokeWidth="1.5" fill="none" />
        <circle cx="280" cy="160" r="80" stroke="white" strokeWidth="1" fill="none" opacity="0.7" />
        <path d="M120 180 L200 120 L280 180 L360 140" stroke="white" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.8" />
        <circle cx="120" cy="220" r="40" stroke="white" strokeWidth="1" fill="none" opacity="0.6" />
      </svg>
    </div>
  );
}

function StructureIllustration(): JSX.Element {
  return (
    <svg viewBox="0 0 280 200" fill="none" className="w-full h-auto max-h-56" aria-hidden="true">
      <path
        d="M20 100 L80 100 L80 60 L140 60 L140 100 L200 100 L200 60 L260 60"
        stroke="var(--smb-ink)"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="20" cy="100" r="8" fill="var(--smb-palette-navy)" />
      <circle cx="80" cy="100" r="8" fill="var(--smb-palette-navy)" />
      <circle cx="80" cy="60" r="8" fill="var(--smb-palette-medium-blue)" />
      <circle cx="140" cy="60" r="8" fill="var(--smb-palette-medium-blue)" />
      <circle cx="140" cy="100" r="8" fill="var(--smb-palette-teal)" />
      <circle cx="200" cy="100" r="8" fill="var(--smb-palette-teal)" />
      <circle cx="200" cy="60" r="8" fill="var(--smb-palette-teal)" />
      <circle cx="260" cy="60" r="10" fill="var(--smb-palette-teal)" strokeWidth="2" stroke="var(--smb-palette-navy)" />
    </svg>
  );
}

function CtaPattern(): JSX.Element {
  return (
    <svg className="absolute inset-0 h-full w-full" aria-hidden="true">
      <defs>
        <pattern id="cta-dots" width="24" height="24" patternUnits="userSpaceOnUse">
          <circle cx="12" cy="12" r="1" fill="rgba(255,255,255,0.08)" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#cta-dots)" />
    </svg>
  );
}

interface BrandingSettings {
  brandName?: string;
  brandTagline?: string;
  logoUrl?: string;
  metaDescription?: string;
}

interface HomePageProps {
  branding: BrandingSettings | null;
  /** Opens the Book a Strategy Call modal. When provided, hero and CTA CTAs use it. */
  onOpenStrategyCall?: () => void;
}

const FRAMEWORK_STEPS = [
  { icon: FileCheck, label: 'Capability Review', short: 'Capability alignment before outreach' },
  { icon: Target, label: 'Prime Alignment', short: 'Contract vehicle relevance' },
  { icon: Users, label: 'Targeted Prime ID', short: 'Targeted Prime identification' },
  { icon: Mail, label: 'Professional Outreach', short: 'Professional messaging and positioning' },
  { icon: ClipboardList, label: 'Structured Follow-up', short: 'Structured follow-up cadence' },
  { icon: Calendar, label: 'Meeting Prep', short: 'AI-assisted meeting preparation support' },
];

export function HomePage({ branding, onOpenStrategyCall }: HomePageProps): JSX.Element {
  const brandName = branding?.brandName ?? 'SMBPrime';
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const faqs = [
    {
      q: 'How long does it take to secure meetings?',
      a: 'Timelines vary based on industry alignment and responsiveness. Structured outreach typically requires several weeks of disciplined engagement.',
    },
    {
      q: 'Do you guarantee contracts?',
      a: 'No. We facilitate qualified conversations. Contract awards depend on multiple factors.',
    },
    {
      q: 'How is this different from hiring a BD employee?',
      a: 'We provide structured Prime engagement without expanding internal headcount. You also gain AI-enabled research workflows and VA-assisted execution support without full-time overhead.',
    },
    {
      q: 'Do you work with startups?',
      a: 'No. We focus on established federal contractors with past performance.',
    },
  ];

  return (
    <main id="main-content" className="font-sans text-gray-900" role="main" aria-label="Home">
      {/* Hero — full-width hero image background, high-contrast white text */}
      <section
        className="relative text-white overflow-hidden min-h-[28rem] flex items-center"
        style={{ backgroundColor: 'var(--smb-primary)' }}
      >
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${heroImage})` }}
          aria-hidden="true"
        />
        <div
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{
            background: 'linear-gradient(to right, rgba(30,58,95,0.92) 0%, rgba(30,58,95,0.75) 40%, rgba(15,23,42,0.7) 100%)',
          }}
          aria-hidden="true"
        />
        <HeroPattern />
        <HeroIllustration />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center lg:text-left">
          <div className="max-w-3xl">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white">
              We Secure Qualified Prime Contractor Meetings
            </h1>
            <p className="mt-4 text-xl sm:text-2xl font-medium text-white/95">
              So You Can Grow Through Federal Subcontracting.
            </p>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-white/90">
              For established federal contractors ready to approach Prime relationships with structure,
              focus, and discipline — without hiring a full internal business development team.
            </p>
            <p className="mt-3 text-sm sm:text-base leading-relaxed text-white/80">
              Human-led execution, enhanced by AI-enabled research and VA-assisted workflow support.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              {onOpenStrategyCall ? (
                <button
                  type="button"
                  onClick={onOpenStrategyCall}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#1e3a5f] bg-white text-[#1e3a5f] hover:bg-gray-100"
                >
                  Book a Strategy Call
                  <ArrowRight size={18} aria-hidden="true" />
                </button>
              ) : (
                <a
                  href="#cta"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#1e3a5f] bg-white text-[#1e3a5f] hover:bg-gray-100"
                >
                  Book a Strategy Call
                  <ArrowRight size={18} aria-hidden="true" />
                </a>
              )}
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border-2 border-white text-white font-semibold hover:bg-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#1e3a5f]"
              >
                See How It Works
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* The Reality of Prime Outreach — imagery + 2026 theme */}
      <section className="bg-white border-b border-smb-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-7">
              <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
                Prime Outreach Reality
              </p>
              <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
                Structure turns outreach into a predictable pipeline
              </h2>
              <p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
                Breaking into aligned prime contractor relationships is not about more introductions.
                It&apos;s about consistent, targeted execution.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-smb-slate-100 bg-smb-slate-100/40 p-5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-smb-slate-800">Without structure</p>
                  <ul className="mt-3 space-y-2.5 text-smb-slate-600">
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Your company stays invisible to aligned primes
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Outreach efforts produce inconsistent responses
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Follow-up gaps stall momentum and timing
                    </li>
                  </ul>
                </div>

                <div className="relative overflow-hidden rounded-xl border-2 border-[#224896]/50 bg-gradient-to-br from-[#224896]/10 via-white to-[#15285f]/10 p-5 shadow-md shadow-smb-primary/10">
                  <div className="absolute -top-12 -right-10 h-28 w-28 rounded-full bg-smb-primary-light/15 blur-xl" aria-hidden="true" />
                  <div className="relative">
                    <p className="inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-bold uppercase tracking-wide text-white bg-[#224896]">
                      With SMBPrime
                    </p>
                    <ul className="mt-4 space-y-3 text-smb-slate-800">
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Build a focused list of prime targets
                      </li>
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Run a repeatable outreach cadence
                      </li>
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Track touchpoints and next actions with discipline
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-8 border-l-4 border-[#15285f] pl-4 sm:pl-5">
                <p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
                  Federal subcontracting is competitive.
                </p>
                <p className="mt-1 text-lg sm:text-xl text-smb-slate-800 font-medium">
                  Winning teams execute with structure.
                </p>
              </div>

              <a
                href="#how-it-works"
                className="mt-7 inline-flex items-center gap-2 text-base sm:text-lg font-semibold text-smb-primary hover:text-smb-primary-mid transition-colors"
              >
                See the Outreach Framework
                <ArrowRight size={18} aria-hidden="true" />
              </a>
            </div>

            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <div className="w-full max-w-sm rounded-2xl border border-smb-slate-100 bg-gradient-to-br from-white to-smb-slate-100/40 p-5 sm:p-6 shadow-sm">
                <div className="w-72 sm:w-80 mx-auto text-smb-primary">
                  <StructureIllustration />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do — two-column with illustration, same treatment as Prime Outreach */}
      <section className="bg-smb-slate-100/50 border-b border-smb-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1 flex justify-center lg:justify-start">
              <div className="w-full max-w-lg rounded-2xl border border-smb-slate-100 shadow-md shadow-smb-primary/5 overflow-hidden">
                <img
                  src={handshakeImage}
                  alt="Partnership and access — handshake"
                  className="w-full h-auto max-h-[32rem] object-cover object-center"
                />
              </div>
            </div>
            <div className="lg:col-span-7 order-1 lg:order-2">
              <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
                Our Role
              </p>
              <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
                Your dedicated Prime engagement partner
              </h2>
              <p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
                {brandName} focuses on federal subcontracting growth — without selling software,
                generic lists, or mass emails.
              </p>
              <p className="mt-3 text-base sm:text-lg text-smb-slate-600 leading-relaxed max-w-2xl">
                Our team combines senior BD judgment with AI-enabled preparation and disciplined VA execution.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-smb-slate-100 bg-smb-slate-100/40 p-5">
                  <p className="text-sm font-semibold uppercase tracking-wide text-smb-slate-800">We don&apos;t</p>
                  <ul className="mt-3 space-y-2.5 text-smb-slate-600">
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Sell software
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Send generic lead lists
                    </li>
                    <li className="flex items-start gap-2">
                      <XCircle size={18} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                      Mass email capability statements
                    </li>
                  </ul>
                </div>

                <div className="relative overflow-hidden rounded-xl border-2 border-[#224896]/50 bg-gradient-to-br from-[#224896]/10 via-white to-[#15285f]/10 p-5 shadow-md shadow-smb-primary/10">
                  <div className="absolute -top-12 -right-10 h-28 w-28 rounded-full bg-smb-primary-light/15 blur-xl" aria-hidden="true" />
                  <div className="relative">
                    <p className="inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-bold uppercase tracking-wide text-white bg-[#224896]">
                      We do
                    </p>
                    <ul className="mt-4 space-y-3 text-smb-slate-800">
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                        The structured work to secure aligned conversations
                      </li>
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                        You focus on delivering performance
                      </li>
                      <li className="flex items-start gap-2.5">
                        <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                        We focus on opening the right doors
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="mt-8 border-l-4 border-[#15285f] pl-4 sm:pl-5">
                <p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
                  Dedicated engagement. No fluff.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Structured Prime Engagement Framework — 2026 theme */}
      <section id="how-it-works" className="bg-white border-b border-smb-slate-100 scroll-mt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
            The System
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
            Our Structured Prime Engagement Framework™
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
            {brandName} operates using a defined system built specifically for federal subcontracting.
          </p>

          <div className="mt-8 relative overflow-hidden rounded-xl border-2 border-[#224896]/50 bg-gradient-to-br from-[#224896]/10 via-white to-[#15285f]/10 p-5 sm:p-6 shadow-md shadow-smb-primary/10">
            <div className="absolute -top-12 -right-10 h-28 w-28 rounded-full bg-smb-primary-light/15 blur-xl" aria-hidden="true" />
            <div className="relative">
              <p className="inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-bold uppercase tracking-wide text-white bg-[#224896] mb-4">
                Our framework ensures
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 text-smb-slate-800">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Capability alignment before outreach
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Contract vehicle relevance
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Targeted Prime identification
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Professional messaging and positioning
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Structured follow-up cadence
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Meeting preparation support
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 border-l-4 border-[#15285f] pl-4 sm:pl-5">
            <p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
              Every engagement is focused, aligned, and deliberate.
            </p>
            <p className="mt-1 text-lg text-smb-slate-600">
              No randomness. No noise. No wasted outreach.
            </p>
          </div>

          {/* 6-step process diagram */}
          <div className="mt-12 pt-10 border-t border-smb-slate-100">
            <p className="text-sm font-semibold uppercase tracking-wide text-smb-slate-600 mb-6">Process at a glance</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {FRAMEWORK_STEPS.map(({ icon: Icon, label, short }, i) => (
                <div
                  key={label}
                  className="flex items-start gap-3 p-4 rounded-xl bg-smb-slate-100/60 border border-smb-slate-100"
                >
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-smb-primary-light/20 text-smb-primary flex items-center justify-center">
                    <Icon size={20} aria-hidden="true" />
                  </div>
                  <div>
                    <p className="font-semibold text-smb-ink">
                      {i + 1}. {label}
                    </p>
                    <p className="text-sm text-smb-slate-600 mt-0.5">{short}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why This Approach Works — 2026 theme */}
      <section className="bg-smb-slate-100/50 border-b border-smb-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
            Why It Works
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
            Why This Approach Works
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
            Prime Contractors engage when the process is structured.
          </p>

          <div className="mt-8 relative overflow-hidden rounded-xl border-2 border-[#224896]/50 bg-gradient-to-br from-[#224896]/10 via-white to-[#15285f]/10 p-5 sm:p-6 shadow-md shadow-smb-primary/10">
            <div className="absolute -top-12 -right-10 h-28 w-28 rounded-full bg-smb-primary-light/15 blur-xl" aria-hidden="true" />
            <div className="relative">
              <p className="inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-bold uppercase tracking-wide text-white bg-[#224896] mb-4">
                Primes engage when
              </p>
              <ul className="space-y-2.5 text-smb-slate-800">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Capabilities clearly align with active contracts
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Past performance is positioned correctly
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Outreach is relevant and professional
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Follow-ups are consistent and disciplined
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 border-l-4 border-[#15285f] pl-4 sm:pl-5">
            <p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
              Most subcontracting efforts fail because they lack structure.
            </p>
            <p className="mt-1 text-lg text-smb-slate-600">
              We bring focus and execution to a process that is often fragmented.
            </p>
          </div>
        </div>
      </section>

      {/* How It Helps You — 2026 theme */}
      <section className="bg-white border-b border-smb-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
            Your Advantage
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
            How It Helps You
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
            When Prime outreach becomes structured, everything changes.
          </p>

          <div className="mt-8 relative overflow-hidden rounded-xl border-2 border-[#224896]/50 bg-gradient-to-br from-[#224896]/10 via-white to-[#15285f]/10 p-5 sm:p-6 shadow-md shadow-smb-primary/10">
            <div className="absolute -top-12 -right-10 h-28 w-28 rounded-full bg-smb-primary-light/15 blur-xl" aria-hidden="true" />
            <div className="relative">
              <p className="inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-bold uppercase tracking-wide text-white bg-[#224896] mb-4">
                You
              </p>
              <ul className="space-y-2.5 text-smb-slate-800">
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Stop chasing misaligned leads
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Save internal BD time
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Engage real decision-makers
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Build consistent Prime relationships
                </li>
                <li className="flex items-start gap-2.5">
                  <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Move from reactive bidding to intentional growth
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 border-l-4 border-[#15285f] pl-4 sm:pl-5">
            <p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
              Subcontracting becomes strategic — not accidental.
            </p>
          </div>
        </div>
      </section>

      {/* Who This Is For — one section, same look and feel as rest of page */}
      <section className="bg-smb-slate-100/50 border-b border-smb-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
            Ideal Fit
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
            Who This Is For
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
            {brandName} is built for established federal contractors ready for structured growth.
          </p>

          {/* Three pillars in same card style as rest of page — who we work with: Targeted, Ready, Partnership */}
          <p className="mt-8 text-sm font-semibold uppercase tracking-wide text-smb-slate-600">
            Who we work with
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-xl border border-smb-slate-100 bg-white p-4 sm:p-5 flex items-center gap-4 shadow-sm">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-smb-primary-light/15 text-smb-primary flex items-center justify-center">
                <Target size={24} strokeWidth={2} aria-hidden="true" />
              </div>
              <span className="text-smb-ink font-semibold">Targeted</span>
            </div>
            <div className="rounded-xl border border-smb-slate-100 bg-white p-4 sm:p-5 flex items-center gap-4 shadow-sm">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#224896]/15 text-[#224896] flex items-center justify-center">
                <FileCheck size={24} strokeWidth={2} aria-hidden="true" />
              </div>
              <span className="text-smb-ink font-semibold">Ready</span>
            </div>
            <div className="rounded-xl border border-smb-slate-100 bg-white p-4 sm:p-5 flex items-center gap-4 shadow-sm">
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-smb-primary-light/15 text-smb-primary flex items-center justify-center">
                <Users size={24} strokeWidth={2} aria-hidden="true" />
              </div>
              <span className="text-smb-ink font-semibold">Partnership</span>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="relative overflow-hidden rounded-xl border-2 border-[#224896]/50 bg-gradient-to-br from-[#224896]/10 via-white to-[#15285f]/10 p-5 shadow-md shadow-smb-primary/10">
              <div className="absolute -top-12 -right-10 h-28 w-28 rounded-full bg-smb-primary-light/15 blur-xl" aria-hidden="true" />
              <div className="relative">
                <p className="inline-flex items-center rounded-full px-3 py-1 text-xs sm:text-sm font-bold uppercase tracking-wide text-white bg-[#224896] mb-4">
                  Built for
                </p>
                <ul className="space-y-2.5 text-smb-slate-800">
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                    Federal contractors generating $5M–$50M annually
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                    Companies with past performance
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                    Teams serious about structured subcontracting growth
                  </li>
                  <li className="flex items-start gap-2.5">
                    <CheckCircle2 size={20} className="text-[#224896] flex-shrink-0 mt-0.5" aria-hidden="true" />
                    Businesses seeking disciplined execution
                  </li>
                </ul>
              </div>
            </div>

            <div className="rounded-xl border border-smb-slate-100 bg-smb-slate-100/40 p-5">
              <p className="text-sm font-semibold uppercase tracking-wide text-smb-slate-800">Not for</p>
              <ul className="mt-3 space-y-2.5 text-smb-slate-600">
                <li className="flex items-start gap-2">
                  <XCircle size={18} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Early-stage startups
                </li>
                <li className="flex items-start gap-2">
                  <XCircle size={18} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  Companies looking for instant contract wins
                </li>
              </ul>
              <p className="mt-4 text-smb-slate-800 font-medium">
                We work with serious operators who value structure.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What We Are Not — 2026 theme */}
      <section className="bg-white border-b border-smb-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
            Clarity
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
            What We Are Not
          </h2>

          <div className="mt-8 rounded-xl border border-smb-slate-100 bg-smb-slate-100/40 p-5 sm:p-6">
            <ul className="space-y-2.5 text-smb-slate-700">
              <li className="flex items-start gap-2.5">
                <XCircle size={20} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                We are not a mass email agency.
              </li>
              <li className="flex items-start gap-2.5">
                <XCircle size={20} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                We do not sell generic lead lists.
              </li>
              <li className="flex items-start gap-2.5">
                <XCircle size={20} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                We do not promise guaranteed contract awards.
              </li>
              <li className="flex items-start gap-2.5">
                <XCircle size={20} className="text-smb-slate-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
                We do not take every company that inquires.
              </li>
            </ul>
          </div>

          <div className="mt-8 border-l-4 border-[#15285f] pl-4 sm:pl-5">
            <p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
              We focus on qualified alignment and professional engagement.
            </p>
            <p className="mt-1 text-lg text-smb-slate-600">
              Subcontracting growth requires consistency — not shortcuts.
            </p>
          </div>
        </div>
      </section>

      {/* Built With Execution in Mind — 2026 theme */}
      <section className="bg-smb-slate-100/50 border-b border-smb-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
            Our Principle
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
            Built With Execution in Mind
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-smb-slate-600 leading-relaxed max-w-2xl">
            {brandName} was created around one principle: structured execution drives growth.
          </p>
          <p className="mt-4 text-smb-slate-700">
            Federal subcontracting is too important to approach casually. We focus exclusively on Prime engagement within the federal ecosystem.
          </p>
          <div className="mt-8 border-l-4 border-[#15285f] pl-4 sm:pl-5">
            <p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
              No distractions. No side markets. No diluted focus.
            </p>
          </div>
        </div>
      </section>

      {/* Important Expectations — 2026 theme (site palette warning tint) */}
      <section className="bg-smb-warning/10 border-y border-smb-warning/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-smb-warning">
            Expectations
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-smb-ink max-w-3xl">
            Important Expectations
          </h2>
          <p className="mt-5 text-lg sm:text-xl text-smb-slate-700 font-medium">
            We do not guarantee contract awards.
          </p>
          <p className="mt-3 text-smb-slate-600 leading-relaxed">
            We secure qualified conversations with aligned Prime Contractors. Subcontracting success depends on capability, pricing, and long-term relationship development.
          </p>
          <div className="mt-8 border-l-4 border-smb-warning pl-4 sm:pl-5">
            <p className="text-xl sm:text-2xl font-semibold text-smb-ink leading-snug">
              Our role is to create structured opportunity — not unrealistic promises.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA — dark background, high-contrast text and buttons */}
      <section
        id="cta"
        className="relative text-white scroll-mt-20 overflow-hidden"
        style={{ backgroundColor: 'var(--smb-ink)' }}
      >
        <CtaPattern />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white">
            Stop chasing Prime Contractors.
          </h2>
          <p className="mt-2 text-xl text-white/90">
            Start meeting the right ones.
          </p>
          <p className="mt-6 text-white/85 max-w-xl mx-auto">
            If you are serious about disciplined subcontracting growth, let&apos;s talk.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            {onOpenStrategyCall ? (
              <button
                type="button"
                onClick={onOpenStrategyCall}
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white text-[#0f172a] font-semibold hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#0f172a]"
              >
                Book a Strategy Call
                <ArrowRight size={18} aria-hidden="true" />
              </button>
            ) : (
              <a
                href="#cta"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-white text-[#0f172a] font-semibold hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#0f172a]"
              >
                Book a Strategy Call
                <ArrowRight size={18} aria-hidden="true" />
              </a>
            )}
            <Link
              to="/about-us"
              className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg border-2 border-white text-white font-semibold hover:bg-white/15 transition-colors focus:outline-none focus:ring-2 focus:ring-white/40 focus:ring-offset-2 focus:ring-offset-[#0f172a]"
            >
              See If You&apos;re a Fit
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ — 2026 theme */}
      <section className="bg-white border-t border-smb-slate-100" aria-labelledby="faq-heading">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
          <p className="text-sm sm:text-base font-semibold tracking-wide uppercase text-[#224896]">
            FAQ
          </p>
          <h2 id="faq-heading" className="mt-2 text-3xl sm:text-4xl font-bold text-smb-ink flex items-center gap-2">
            <HelpCircle size={28} className="text-smb-primary-mid" aria-hidden="true" />
            Frequently Asked Questions
          </h2>
          <div className="mt-8 space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="border border-smb-slate-100 rounded-xl overflow-hidden bg-smb-slate-100/30"
              >
                <button
                  type="button"
                  onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-4 py-4 text-left font-medium text-smb-ink hover:bg-smb-slate-100/50 transition-colors focus:outline-none focus:ring-2 focus:ring-smb-primary-mid focus:ring-inset"
                  aria-expanded={openFaqIndex === i}
                  aria-controls={`faq-answer-${i}`}
                  id={`faq-question-${i}`}
                >
                  {faq.q}
                  <ChevronDown
                    size={20}
                    className={`flex-shrink-0 text-smb-slate-600 transition-transform ${openFaqIndex === i ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                <div
                  id={`faq-answer-${i}`}
                  role="region"
                  aria-labelledby={`faq-question-${i}`}
                  className={`overflow-hidden transition-all duration-200 ${openFaqIndex === i ? 'max-h-48' : 'max-h-0'}`}
                >
                  <p className="px-4 pb-4 pt-0 text-smb-slate-600">{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
